import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';

export interface UsuarioApi {
  id: number;
  name: string;
  email: string;
  role: 'admin' | 'user' | 'consultor';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt: string | null;
}

interface UsuariosResponse {
  success: boolean;
  data: {
    users: UsuarioApi[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
  };
}

interface UsuarioResponse {
  success: boolean;
  data: {
    user: UsuarioApi;
  };
}

interface CreateUsuarioInput {
  name: string;
  email: string;
  role: 'admin' | 'user' | 'consultor';
  password: string;
}

interface UpdateUsuarioInput {
  id: number;
  name?: string;
  email?: string;
  role?: 'admin' | 'user' | 'consultor';
  password?: string;
  isActive?: boolean;
}

export function useUsuarios(params?: { search?: string; isActive?: boolean; role?: 'admin' | 'user' | 'consultor'; page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['usuarios', params],
    queryFn: async () => {
      const response = await api.get<UsuariosResponse>('/users', {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 20,
          ...(params?.search ? { search: params.search } : {}),
          ...(params?.isActive !== undefined ? { isActive: String(params.isActive) } : {}),
          ...(params?.role ? { role: params.role } : {})
        }
      });
      return response.data;
    }
  });
}

export function useCriarUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateUsuarioInput) => {
      const response = await api.post<UsuarioResponse>('/users', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    }
  });
}

export function useAtualizarUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateUsuarioInput) => {
      const response = await api.patch<UsuarioResponse>(`/users/${id}`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    }
  });
}

export function useDesativarUsuario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/users/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usuarios'] });
    }
  });
}
